import { useAbility } from '@casl/react'
import { PureAbility, RawRule } from '@casl/ability'
import React from 'react'
import type { Action, Subject, SubjectField } from 'polaris-adventures-js'

export function usePermissions() {
  const ability = useAbility(AbilityContext)

  const can = React.useCallback(
    <S extends Subject>(action: Action<S>, subject: S, field?: SubjectField<S> | undefined | null) => {
      return ability.can(action, subject, field)
    },
    [ability]
  )
  const cannot = React.useCallback(
    <S extends Subject>(action: Action<S>, subject: S, field?: SubjectField<S> | undefined | null) => {
      return ability.cannot(action, subject, field)
    },
    [ability]
  )

  return { can, cannot }
}

export function CanWrapper<S extends Subject>({
  I,
  a,
  field,
  fallback = null,
  children = null
}: {
  I: Action<S>
  a: S
  field?: SubjectField<S> | undefined | null
  children?: React.ReactNode
  fallback?: React.ReactNode
}) {
  const { can } = usePermissions()

  if (can(I, a, field)) {
    return <>{children}</>
  } else {
    return <>{fallback}</>
  }
}

const AbilityContext = React.createContext<PureAbility>(new PureAbility([]))

export function PermissionsProvider({ rules, children }: { rules?: RawRule[]; children?: React.ReactNode }) {
  const ability = React.useMemo(() => {
    return new PureAbility(rules ?? [], { fieldMatcher: (fields) => (field) => fields.includes(field) })
  }, [rules])
  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
}
